/**
 * Utility for parsing mailing address strings into structured components
 */

export interface ParsedAddress {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
}

/**
 * Parse mailing address string into components
 *
 * Formats supported:
 * - "addressLine1, city, state, zipCode, country" (5 parts)
 * - "addressLine1, addressLine2, city, state, zipCode, country" (6 parts)
 *
 * @param mailingAddress - Comma-separated address string from API
 * @returns Parsed address components
 */
export const parseMailingAddress = (mailingAddress: string): ParsedAddress => {
    if (!mailingAddress) {
        return {
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zipCode: "",
        };
    }

    // Split by comma and trim each part
    const parts = mailingAddress.split(",").map((part) => part.trim());

    // Parse based on number of parts
    if (parts.length >= 6) {
        // Format: addressLine1, addressLine2, city, state, zipCode, country
        return {
            addressLine1: parts[0] || "",
            addressLine2: parts[1] || "",
            city: parts[2] || "",
            state: parts[3] || "",
            zipCode: parts[4] || "",
        };
    } else if (parts.length === 5) {
        // Format: addressLine1, city, state, zipCode, country
        return {
            addressLine1: parts[0] || "",
            addressLine2: "",
            city: parts[1] || "",
            state: parts[2] || "",
            zipCode: parts[3] || "",
        };
    } else {
        // Fallback for unexpected format
        return {
            addressLine1: parts[0] || "",
            addressLine2: "",
            city: parts[1] || "",
            state: parts[2] || "",
            zipCode: parts[3] || "",
        };
    }
};
